import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { useNodeMapActions, useNodeMapShallowSelector } from 'components/WorkspaceEditor/features/keyOperations'
import { useNotification, useTransactionManager } from 'components/WorkspaceEditor/services'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { DESIGNATION_PLACEHOLDER } from '../constants'
import { createOptimisticDesignationUpdate } from '../services/optimisticUpdater'
import { PRODUCT_TYPE_ID_ERROR_MESSAGES, resolveProductTypeIds } from '../utils/resolveProductTypeIds'
import { useDrawingDesignationMutation } from './useDrawingDesignationMutation'


interface UseDrawingDesignationHandlersProps {
    currentDesignation: string
    contentItemId: string
}

interface DrawingDesignationHandlers {
    handleValueChange: (
        updatedItem: Partial<TextContentElement>,
        isSelected: boolean
    ) => Promise<void>
    isSaving: boolean
    /** Whether the designation can be edited (false when required IDs are missing or invalid) */
    canEditDesignation: boolean
}

/**
 * Hook that provides handlers for drawing designation changes with
 * race condition prevention, optimistic updates, and error handling.
 */
export const useDrawingDesignationHandlers = ({
    currentDesignation,
    contentItemId,
}: UseDrawingDesignationHandlersProps): DrawingDesignationHandlers => {
    const { error } = useNotification()
    const { executeWithOptimistic } = useTransactionManager()

    const {
        selectedProjectElementNode,
        projectId,
        isTreeMappingInProgress,
        contentItemViewNodes,
        setContentItemViewNodes,
        familyAddedProductTypes,
        markElementAsBeingProcessed,
        markElementAsRecentlyProcessed,
        isElementBeingProcessed,
        isElementRecentlyProcessed,
        elementsBeingProcessed,
    } = useShallowSelector((state) => ({
        selectedProjectElementNode: state.selectedProjectElementNode,
        projectId: state.projectId,
        isTreeMappingInProgress: state.isTreeMappingInProgress,
        contentItemViewNodes: state.contentItemViewNodes,
        setContentItemViewNodes: state.actions.setContentItemViewNodes,
        familyAddedProductTypes: state.familyAddedProductTypes || [],
        markElementAsBeingProcessed: state.actions.markElementAsBeingProcessed,
        markElementAsRecentlyProcessed: state.actions.markElementAsRecentlyProcessed,
        isElementBeingProcessed: state.actions.isElementBeingProcessed,
        isElementRecentlyProcessed: state.actions.isElementRecentlyProcessed,
        elementsBeingProcessed: state.elementsBeingProcessed, // For re-render on state change
    }))

    const { parentFamilyId } = useElementItemShallowSelector((s) => ({
        parentFamilyId: s.elementItemValue.parentFamilyId,
    }))

    const { isSaveOperationInProgress } = useNodeMapShallowSelector(
        (state) => ({
            isSaveOperationInProgress: state.isSaveOperationInProgress,
        })
    )
    const { setSaveOperationInProgress } = useNodeMapActions()

    const { saveDesignation, isSaving } = useDrawingDesignationMutation()
    const lastSavedDesignationRef = useRef(currentDesignation)

    // Sync ref with server state for change detection
    useEffect(() => {
        lastSavedDesignationRef.current = currentDesignation
    }, [currentDesignation])

    // Resolve and validate product type IDs
    const resolvedIds = useMemo(() => {
        if (!projectId) {
            return { canEdit: false, productTypeElementId: '', productFamilyId: '', error: undefined }
        }

        const {
            productTypeElementId,
            productFamilyId,
            error: resolveError,
        } = resolveProductTypeIds({
            selectedProjectElementNode,
            contentItemViewNodes,
            contentItemId,
            familyAddedProductTypes,
            parentFamilyId,
        })

        const canEdit = !resolveError && !!productTypeElementId && !!productFamilyId

        return { canEdit, productTypeElementId, productFamilyId, error: resolveError }
    }, [
        projectId,
        selectedProjectElementNode,
        contentItemViewNodes,
        contentItemId,
        familyAddedProductTypes,
        parentFamilyId,
    ])

    const handleValueChange = useCallback(
        async (updatedItem: Partial<TextContentElement>, _isSelected: boolean) => {
            // Skip if element is currently or recently being processed
            const isElementBusy = isElementBeingProcessed?.(contentItemId) || isElementRecentlyProcessed?.(contentItemId)
            if (isElementBusy) {
                return
            }

            const rawText = updatedItem.text?.trim() || ''

            const newDesignation = rawText === DESIGNATION_PLACEHOLDER ? '' : rawText

            // Skip if system busy or no change
            const isSystemBusy = isSaveOperationInProgress || (isTreeMappingInProgress ?? false)
            if (isSystemBusy) return
            if (newDesignation === lastSavedDesignationRef.current) return

            const { canEdit, productTypeElementId, productFamilyId, error: resolveError } = resolvedIds

            if (resolveError) {
                error({ message: PRODUCT_TYPE_ID_ERROR_MESSAGES[resolveError] })
                setSaveOperationInProgress(false)
                return
            }

            if (!canEdit || !projectId) {
                error({ message: 'Missing required information to save drawing designation' })
                setSaveOperationInProgress(false)
                return
            }

            const optimisticTreeViewNodes = createOptimisticDesignationUpdate(
                contentItemViewNodes,
                contentItemId,
                newDesignation
            )

            // Mark as processing before async operation to prevent race conditions
            markElementAsBeingProcessed?.(contentItemId)
            setSaveOperationInProgress(true)

            executeWithOptimistic({
                fallbackState: '',
                asyncAction: async () => {
                    await saveDesignation({
                        productTypeElementId,
                        projectId,
                        designation: newDesignation,
                        productFamilyId,
                    })
                },
                successCbFn: async () => {
                    lastSavedDesignationRef.current = newDesignation
                    markElementAsRecentlyProcessed?.(contentItemId)
                    setSaveOperationInProgress(false)
                },
                errorCbFn: () => {
                    markElementAsRecentlyProcessed?.(contentItemId)
                    setSaveOperationInProgress(false)
                    error({ message: 'Failed to save drawing designation' })
                },
            })

            setContentItemViewNodes(optimisticTreeViewNodes)
        },
        [
            contentItemId,
            isElementBeingProcessed,
            isElementRecentlyProcessed,
            isSaveOperationInProgress,
            isTreeMappingInProgress,
            projectId,
            resolvedIds,
            contentItemViewNodes,
            saveDesignation,
            executeWithOptimistic,
            markElementAsBeingProcessed,
            markElementAsRecentlyProcessed,
            setSaveOperationInProgress,
            setContentItemViewNodes,
            error,
        ]
    )

    const isCurrentElementBeingProcessed = elementsBeingProcessed?.has(contentItemId) ?? false

    return {
        handleValueChange,
        isSaving: isSaving || isSaveOperationInProgress || isCurrentElementBeingProcessed,
        canEditDesignation: resolvedIds.canEdit,
    }
}
